import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import { useLocation, useNavigate } from "react-router-dom";
import { FeatureType } from "../../constants/FeatureToggles";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBInput,
} from "mdb-react-ui-kit";
import { Box, Tooltip, IconButton } from "@mui/material";
import {
  Edit as EditIcon,
  Visibility,
  VisibilityOff,
  Delete,
  Download,
} from "@mui/icons-material";
import AbsModal from "../../common/absModal/AbsModal";
import { ClaimsContext } from "../../services/ClaimsService";
import "./Help.scss";
import "./Guides.scss";
import { DragDropStyledContainer } from "../../common/dropFiles/dragdropfiles";
import { AttachmentsUpload } from "abs-ui-components";
import BreadCrumb from "../../common/breadCrumb/BreadCrumb";
import { HELP_DATA } from "./constants";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import { useSelector } from "react-redux";
import { isErSuperAdmin } from "../../utils/roleUtils";

const PRIMARY_BUTTON_COLOR = '#002A4E';

const Guides = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.userInfo.userData);
  const isSuperAdmin = useMemo(() => isErSuperAdmin(user), [user]);

  const canAdmin = isSuperAdmin;

  const [guides, setGuides] = useState([]);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([]);
  const [loading, setLoading] = useState(false);

  const [deleteGuideOpen, setDeleteGuideOpen] = useState(false);
  const [guideToDelete, setGuideToDelete] = useState({ id: null, title: "" });

  const [downloading, setDownloading] = useState(() => new Set());
  const [visibilityLoading, setVisibilityLoading] = useState(() => new Set());
  const [deletingLoading, setDeletingLoading] = useState(() => new Set());
  const [modalDeleting, setModalDeleting] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formTitle, setFormTitle] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [originalFileName, setOriginalFileName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  /* --------------------------- Feature Toggle -------------------------- */
      const location = useLocation();
      const wcnNumber = location.state?.wcnNumber;
  
      const { userHasFeature, userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta } =
      useContext(ClaimsContext);
  
      useEffect(() => {
          if (!userFeaturesAreLoaded || checkWcnDelta(wcnNumber)) {
            fetchUserFeatures(wcnNumber);
          }
        }, [userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta, wcnNumber]);
  
      const canViewGuides = useMemo(() => {
          return userHasFeature(FeatureType.ViewSupportGuides);
      }, [userHasFeature]);
      const canCreateGuides = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.CreateSupportGuide);
      }, [isSuperAdmin, userHasFeature]);
      const canEditGuides = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.EditSupportGuide);
      }, [isSuperAdmin, userHasFeature]);
      const canDownloadGuides = useMemo(() => {
          return userHasFeature(FeatureType.DownloadSupportGuide);
      }, [userHasFeature]);
      const canToggleGuides = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.ToggleGuideVisibility);
      }, [isSuperAdmin, userHasFeature]);
      const canDeleteGuides = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.DeleteSupportGuide);
      }, [isSuperAdmin, userHasFeature]);

  /* --------------------------- Derived Data -------------------------- */
  const breadcrumb = useMemo(
    () => [
      { label: HELP_DATA["nav-support-text"], url: "/support", navId: "support_navId" },
      { label: HELP_DATA["nav-help-text"], url: "/support", navId: "help_navId" },
      { label: "Guides", url: "", navId: "guides_navId" },
    ],
    []
  );

  const tableData = useMemo(() => {
    return guides.filter((g) => (canAdmin ? true : !g.hidden));
  }, [guides, canAdmin]);

  /* --------------------------- API Calls & Callbacks -------------------------- */
  const fetchGuides = useCallback(async () => {
    try {
      setLoading(true);

      const requestBody = {
        globalFilter: search || "",
        filters: [],
        pagination: {
          page: 1,
          pageSize: 50,
        },
        sortOption: {
          SortField: sorting[0]?.id ?? "",
          IsAscending: sorting[0] ? !sorting[0].desc : false,
        },
      };

      const response = await axiosHttp.post("api/Support/GetGuideList", requestBody);
      const payload = Array.isArray(response?.data?.Data) ? response.data.Data : [];
      const mapped = payload.map((g) => ({
        id: g.GuideId,
        title: g.Title,
        description: g.Description ?? "",
        hidden: typeof g.IsViewable === "boolean" ? !g.IsViewable : false,
      }));
      setGuides(mapped);
    } catch (err) {
      console.error("Failed to load guides", err);
      setGuides([]);
    } finally {
      setLoading(false);
    }
  }, [search, sorting]);

  const onDownload = async (guideId, fallbackName = "guide.pdf") => {
    if (!guideId) return;
    try {
      setDownloading((prev) => {
        const next = new Set(prev);
        next.add(guideId);
        return next;
      });

      const response = await axiosHttp.get("api/Support/DownloadGuide", {
        params: { guideId },
        responseType: "blob",
      });

      const blob = response?.data;
      if (!blob) {
        emitToasterMsg("error", "No file data received.");
        return;
      }

      const contentType = response?.headers?.["content-type"] || "application/pdf";
      const disposition = response?.headers?.["content-disposition"];
      const filename = getFilenameFromDisposition(disposition) || fallbackName;

      const fileBlob = new Blob([blob], { type: contentType });
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download guide", err);
      emitToasterMsg("error", "Failed to download guide.");
    } finally {
      setDownloading((prev) => {
        const next = new Set(prev);
        next.delete(guideId);
        return next;
      });
    }
  };

  const onToggleVisibility = async (item) => {
    if (!item?.id) return;
    const guideId = item.id;
    const nextIsViewable = !!item.hidden ? true : false;

    try {
      setVisibilityLoading((prev) => {
        const next = new Set(prev);
        next.add(guideId);
        return next;
      });

      setGuides((prev) =>
        prev.map((g) => (g.id === guideId ? { ...g, hidden: !nextIsViewable } : g))
      );

      const response = await axiosHttp.get("api/Support/ToggleGuideVisibility", {
        params: { guideId, isViewable: nextIsViewable },
      });

      const updated = response?.data?.Data;
      if (updated && typeof updated === "object") {
        setGuides((prev) =>
          prev.map((g) =>
            g.id === (updated.GuideId ?? guideId)
              ? {
                  ...g,
                  title: updated.Title ?? g.title,
                  description: updated.Description ?? g.description,
                  hidden:
                    typeof updated.IsViewable === "boolean"
                      ? !updated.IsViewable
                      : !nextIsViewable,
                }
              : g
          )
        );
      }
    } catch (err) {
      console.error("Failed to toggle guide visibility", err);
      emitToasterMsg("error", "Failed to update guide visibility.");
      setGuides((prev) =>
        prev.map((g) => (g.id === guideId ? { ...g, hidden: !!item.hidden } : g))
      );
    } finally {
      setVisibilityLoading((prev) => {
        const next = new Set(prev);
        next.delete(guideId);
        return next;
      });
    }
  };

  const confirmDeleteGuide = async () => {
    if (!guideToDelete?.id) return;
    const guideId = guideToDelete.id;

    try {
      setDeletingLoading((prev) => {
        const next = new Set(prev);
        next.add(guideId);
        return next;
      });
      setModalDeleting(true);

      const response = await axiosHttp.delete("api/Support/DeleteGuide", {
        params: { guideId },
      });

      if (response?.data?.isSuccess === true) {
        setGuides((prev) => prev.filter((g) => g.id !== guideId));
        emitToasterMsg("success", "Guide deleted successfully.");
      } else {
        emitToasterMsg("error", "Failed to delete guide.");
      }
    } catch (err) {
      console.error("Failed to delete guide", err);
      emitToasterMsg("error", "Failed to delete guide.");
    } finally {
      setDeletingLoading((prev) => {
        const next = new Set(prev);
        next.delete(guideId);
        return next;
      });
      setModalDeleting(false);
      setDeleteGuideOpen(false);
      setGuideToDelete({ id: null, title: "" });
    }
  };

  const onSave = async () => {
    const title = formTitle.trim();
    const description = formDesc.trim();

    if (!title) return;

    const file = selectedFiles[0];
    const hasNewFile = file instanceof File;

    if (!editing && !hasNewFile) {
      emitToasterMsg("error", "Please attach a PDF file.");
      return;
    }
    if (hasNewFile && !file.type?.toLowerCase().includes("pdf")) {
      emitToasterMsg("error", "Only PDF files are allowed.");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("Title", title);
      if (description) formData.append("Description", description);
      if (hasNewFile) formData.append("File", file, file.name);

      let response;
      if (editing) {
        response = await axiosHttp.post("api/Support/EditGuide", formData, {
          params: { guideId: editing.id },
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axiosHttp.post("api/Support/CreateGuide", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const data = response?.data;
      const isSuccess = data?.isSuccess === true || data?.IsSuccess === true;

      if (isSuccess) {
        if (!editing) {
          emitToasterMsg("success", "Guides", "File(s) Uploaded Successfully");
        } else {
          emitToasterMsg("success", "Guides", "Guide updated successfully.");
        }

        setModalOpen(false);
        await fetchGuides();
        resetForm();
      } else {
        const message =
          data?.Message ||
          data?.message ||
          (editing ? "Failed to update guide." : "Failed to upload guide.");
        emitToasterMsg("error", "Documents", message);
      }
    } catch (err) {
      console.error(editing ? "Failed to update guide" : "Failed to create guide", err);
      emitToasterMsg("error", "Documents", editing ? "Failed to update guide." : "Failed to upload guide.");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------- Effects -------------------------- */
  // debounced fetch on search changes
  useEffect(() => {
    const t = setTimeout(() => fetchGuides(), 300);
    return () => clearTimeout(t);
  }, [fetchGuides]);

  /* --------------------------- Table Configuration -------------------------- */
  const titleCellSx = {
    maxWidth: 260,
    width: 260,
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
  };

  const descCellSx = {
    maxWidth: 480,
    width: 480,
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
  };

  const columns = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title",
        size: 260,
        muiTableBodyCellProps: { sx: { maxWidth: 260, width: 260 } },
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() || ""}>
            <Box sx={titleCellSx}>{cell.getValue()}</Box>
          </Tooltip>
        ),
      },
      {
        header: "Description",
        accessorKey: "description",
        size: 480,
        muiTableBodyCellProps: { sx: { maxWidth: 480, width: 480 } },
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() || ""}>
            <Box sx={descCellSx}>{cell.getValue()}</Box>
          </Tooltip>
        ),
      },
      {
        header: "Actions",
        id: "actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const item = row.original;
          const isHidden = !!item.hidden;
          const actionIconColor = "#002a4e";
          const isDownloading = downloading.has(item.id);
          const isToggling = visibilityLoading.has(item.id);
          const isDeleting = deletingLoading.has(item.id);

          return (
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              {canDownloadGuides && (
                <Tooltip title={isDownloading ? "Downloading..." : "Download"}>
                  <span>
                    <IconButton
                      size="small"
                      sx={{ color: actionIconColor, position: "relative", width: 32, height: 32 }}
                      onClick={() => !isDownloading && onDownload(item.id, `${item.title || "guide"}.pdf`)}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : (
                        <Download fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {(canToggleGuides || canEditGuides || canDeleteGuides) && (
                <>
                  {canToggleGuides && (
                    <Tooltip title={isToggling ? "Updating..." : isHidden ? "Show" : "Hide"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => !isToggling && onToggleVisibility(item)}
                          sx={{ position: "relative", width: 32, height: 32 }}
                          disabled={isToggling}
                        >
                          {isToggling ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : isHidden ? (
                            <Visibility fontSize="small" htmlColor="#6c757d" />
                          ) : (
                            <VisibilityOff fontSize="small" htmlColor="#6c757d" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {canEditGuides && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit(item)} sx={{ color: "#6c757d" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {canDeleteGuides && (
                    <Tooltip title={isDeleting ? "Deleting..." : "Delete"}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => !isDeleting && onDelete(item.id)}
                          disabled={isDeleting}
                          sx={{ position: "relative", width: 32, height: 32 }}
                        >
                          {isDeleting ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : (
                            <Delete fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          );
        },
      },
    ],
    [canAdmin, downloading, visibilityLoading, deletingLoading]
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    manualFiltering: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnOrder: ["title", "description", "actions"],
    },
    muiTableContainerProps: { sx: { maxHeight: "calc(100vh - 376px)" } },
    muiTableBodyCellProps: {
      sx: {
        height: 56,
        maxHeight: 56,
        verticalAlign: "middle",
        py: 0.5,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      className: row?.original?.hidden ? "guide-row-hidden" : undefined,
      sx: {
        height: 56,
        maxHeight: 56,
        ...(row?.original?.hidden ? { opacity: 0.6 } : {}),
      },
    }),
    muiTablePaperProps: {
      sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" },
    },
    state: {
      isLoading: loading,
      globalFilter: search,
      sorting,
    },
  });

  /* --------------------------- Handlers & Utilities -------------------------- */
  const getFilenameFromDisposition = (contentDisposition) => {
    if (!contentDisposition) return null;
    try {
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
      if (match && match[1]) return decodeURIComponent(match[1]);
      return null;
    } catch {
      return null;
    }
  };

  const toPdfFileName = (title) => {
    const base = (title || "").trim();
    if (!base) return "";
    return base.toLowerCase().endsWith(".pdf") ? base : `${base}.pdf`;
  };

  const resetForm = () => {
    setEditing(null);
    setFormTitle("");
    setFormDesc("");
    setOriginalFileName("");
    setSelectedFiles([]);
  };

  const openAdd = () => {
    resetForm();
    setModalOpen(true);
  };

  const onEdit = (item) => {
    setEditing(item);
    setFormTitle(item.title);
    setFormDesc(item.description);
    // Backend list doesn't return a filename; Title is effectively the PDF name.
    setOriginalFileName(toPdfFileName(item?.title));
    setSelectedFiles([]);
    setModalOpen(true);
  };

  const onDelete = (id) => {
    const item = guides.find((g) => g.id === id);
    setGuideToDelete({ id, title: item?.title || "" });
    setDeleteGuideOpen(true);
  };

  const cancelDeleteGuide = () => {
    setDeleteGuideOpen(false);
    setGuideToDelete({ id: null, title: "" });
  };

  const handleFilesSelected = (files) => {
    const collected = [];
    files.forEach((f) => {
      const file = f?.file || f;
      if (file && file.type?.toLowerCase().includes("pdf")) {
        collected.push(file);
      }
    });

    if (collected.length === 0) return;

    if (collected.length > 1) {
      emitToasterMsg("warning", "Only one PDF can be attached. Using the first file.");
    }

    setSelectedFiles([collected[0]]);
  };

  const removeFileAt = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* --------------------------- JSX -------------------------- */
  if (!canViewGuides) {
    return (
      <MDBContainer fluid className="px-2 px-md-3 py-4">
        <div className="mb-3">
          <BreadCrumb
            items={breadcrumb}
            onCrumbClick={(item) => {
              if (item.url) navigate(item.url);
            }}
          />
        </div>
        <MDBRow className="gx-2">
          <MDBCol md="12" lg="12">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle className="mb-0">Guides</MDBCardTitle>
                <div className="mt-3">You don’t have permission to view guides.</div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="px-2 px-md-3 py-4">
      {/* Breadcrumb navigation */}
      <div className="mb-3">
        <BreadCrumb
          items={breadcrumb}
          onCrumbClick={(item) => {
            if (item.url) navigate(item.url);
          }}
        />
      </div>

      {/* Page content: guides card */}
      <MDBRow className="gx-2">
        <MDBCol md="12" lg="12">
          <MDBCard className="h-100">
            <MDBCardBody>
              {/* Card header: title + admin actions */}
              <div className="d-flex align-items-center justify-content-between">
                <MDBCardTitle className="mb-0">Guides</MDBCardTitle>
                {canCreateGuides && (
                  <Button
                    onClick={openAdd}
                    startIcon={<Icon icon={ICONS.ADD} />}
                    primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
                    primaryColor={PRIMARY_BUTTON_COLOR}
                  >
                    ADD GUIDE
                  </Button>
                )}
              </div>

              {/* Table section */}
              <div className="list-height">
                <MaterialReactTable
                  table={{
                    ...table,
                    columns: [
                      { header: "Title", accessorKey: "title" },
                      { header: "Description", accessorKey: "description" },
                      {
                        header: "Actions",
                        id: "actions",
                        enableSorting: false,
                        enableColumnFilter: false,
                        Cell: ({ row }) => {
                          const item = row.original;
                          const isHidden = !!item.hidden;
                          const actionIconColor = "#002a4e";
                          const isDownloading = downloading.has(item.id);
                          const isToggling = visibilityLoading.has(item.id);
                          const isDeleting = deletingLoading.has(item.id);

                          return (
                            <Box sx={{ display: "flex", gap: "0.5rem" }}>
                              {canDownloadGuides && (
                                <Tooltip title={isDownloading ? "Downloading..." : "Download"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={{ color: actionIconColor, position: "relative", width: 32, height: 32 }}
                                      onClick={() => !isDownloading && onDownload(item.id, `${item.title || "guide"}.pdf`)}
                                      disabled={isDownloading}
                                    >
                                      {isDownloading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                      ) : (
                                        <Download fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                              {(canToggleGuides || canEditGuides || canDeleteGuides) && (
                                <>
                                  {canToggleGuides && (
                                    <Tooltip title={isToggling ? "Updating..." : isHidden ? "Show" : "Hide"}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => !isToggling && onToggleVisibility(item)}
                                          sx={{ position: "relative", width: 32, height: 32 }}
                                          disabled={isToggling}
                                        >
                                          {isToggling ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                          ) : isHidden ? (
                                            <VisibilityOff fontSize="small" htmlColor="#6c757d" />
                                          ) : (
                                            <Visibility fontSize="small" htmlColor="#6c757d" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}

                                  {canEditGuides && (
                                    <Tooltip title="Edit">
                                      <IconButton size="small" onClick={() => onEdit(item)} sx={{ color: "#6c757d" }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}

                                  {canDeleteGuides && (
                                    <Tooltip title={isDeleting ? "Deleting..." : "Delete"}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => !isDeleting && onDelete(item.id)}
                                          disabled={isDeleting}
                                          sx={{ position: "relative", width: 32, height: 32 }}
                                        >
                                          {isDeleting ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                          ) : (
                                            <Delete fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </Box>
                          );
                        },
                      },
                    ],
                  }}
                />
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      {/* Add/Edit Guide Modal */}
      <AbsModal
        isOpen={modalOpen}
        title={editing ? "Edit Guide" : "Add Guide"}
        onClose={setModalOpen}
        close={() => {
          setModalOpen(false);
          resetForm();
        }}
      >
        {/* Form fields */}
        <div className="mb-3">
          {/* Title field */}
          <MDBInput
            label="Guide Title"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
          />
        </div>

        <div className="mb-3">
          {/* Description field */}
          <MDBInput
            label="Guide Description"
            value={formDesc}
            onChange={(e) => setFormDesc(e.target.value)}
          />
        </div>

        {/* File upload section */}
        <div className="mb-3">
          <DragDropStyledContainer>
            <AttachmentsUpload
              onFilesSelected={handleFilesSelected}
              supportedFormat="PDF only (50 MB limit, single file)"
              supportedFileTypes="pdf"
              multiple={false}
            />
          </DragDropStyledContainer>
        </div>

        {/* Selected files list */}
        {(selectedFiles.length > 0 || !!originalFileName) && (
          <div className="mb-3">
            <div className="selected-files">
              {selectedFiles.length > 0
                ? selectedFiles.map((file, index) => (
                    <div
                      key={`guide-${index}`}
                      className="file-item d-flex justify-content-between align-items-center"
                    >
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0"
                        onClick={() => removeFileAt(index)}
                      >
                        Remove file
                      </button>
                    </div>
                  ))
                : (
                    <div className="file-item d-flex justify-content-between align-items-center">
                      <span>{originalFileName}</span>
                      {/* Intentionally no "Remove" here: removing an already-uploaded guide PDF
                          isn't supported by the current edit API (it only replaces when a new File is attached). */}
                      <span />
                    </div>
                  )}
            </div>
          </div>
        )}

        {/* Modal actions: Add then Cancel (match app styling) */}
        <div className="help-modal-footer">
          <Button
            className="save-button"
            onClick={onSave}
            disabled={saving || !formTitle.trim() || (!editing && selectedFiles.length === 0)}
            primaryBackgroundColor={COLORS.RED.DARK}
            primaryColor={COLORS.RED.DARK}
          >
            <span className="d-inline-flex align-items-center">
              {/* Save button spinner */}
              {saving && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              {editing ? "Save" : "Add"}
            </span>
          </Button>
          <Button
            className="clear-button"
            onClick={() => {
              setModalOpen(false);
              resetForm();
            }}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      </AbsModal>

      {/* Delete confirmation modal */}
      <div className="confirmation-modal-container">
        <Modal
          isOpen={deleteGuideOpen}
          modalHeader
          padding={"70px"}
          headerHeight={"50px"}
          showFooter
          onCloseClickListener={cancelDeleteGuide}
          footer={
            <div className="d-flex justify-content-end fixed-action-btns p-4">
              <Button
                className="modal-delete-btn"
                onClick={confirmDeleteGuide}
                primaryColor={COLORS.RED.DARK}
                primaryBackgroundColor={COLORS.RED.LIGHT}
                disabled={modalDeleting}
              >
                {/* Delete button spinner */}
                {modalDeleting ? (
                  <span className="d-inline-flex align-items-center">
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Deleting...
                  </span>
                ) : (
                  "Delete"
                )}
              </Button>
              <Button className="modal-cancel-btn" onClick={cancelDeleteGuide} disabled={modalDeleting}>
                Cancel
              </Button>
            </div>
          }
        >
          {/* Modal body text */}
          <div>
            Are you sure you want to delete {guideToDelete?.title ? `: "${guideToDelete.title}"` : ""}?
          </div>
        </Modal>
      </div>
    </MDBContainer>
  );
};

export default Guides;